import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { useToggleV2, useWatch } from 'hooks';
import { forwardRef, ReactNode } from 'react';

import { cn } from 'styles';
import { OrString } from 'types';

import { quickSwitch } from 'utils/conditions';
import { localFiles } from 'utils/localFiles';
import { getInitials } from 'utils/text';

// Eval adding withTooltip or even directly support tooltip when name is present

const sizeClasses = {
  tiny: {
    image: 'size-5',
    initials: 'text-xxs',
  },
  small: {
    image: 'size-6',
    initials: 'text-xs',
  },
  medium: {
    image: 'size-8',
    initials: 'text-sm',
  },
  large: {
    image: 'size-10',
    initials: 'text-base',
  },
};

type AvatarSize = keyof typeof sizeClasses;

export type AvatarProps = {
  className?: string;
  // containerClassName?: string;

  src?: string;
  alt?: string;
  name?: string;

  fallback?: OrString<'no-user-avatar' | 'initials'>;
  size?: AvatarSize;
  format?: 'circle' | 'square';
  fallbackLabel?: ReactNode;
};

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  (
    {
      className,
      // containerClassName,
      src,
      alt,
      fallback = 'initials',
      size = 'medium',
      name = '',
      format = 'circle',
      fallbackLabel,
    },
    ref,
  ) => {
    const [hasError, { turnOn, turnOff }] = useToggleV2();
    useWatch(src, turnOff);

    const sizeClass = sizeClasses[size];

    const displayInitials = fallback === 'initials';

    const fallBackSrc = quickSwitch(fallback, [
      { is: 'initials', then: src },
      { is: 'no-user-avatar', then: localFiles.images.noPictureAvatar },
      { is: true, then: fallback },
    ]);

    return (
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          'relative flex shrink-0',
          format === 'circle' ? 'rounded-full' : 'rounded-lg',
          sizeClass.image,
          // containerClassName,
          className,
        )}
      >
        <AvatarPrimitive.Image
          src={hasError ? fallBackSrc : src}
          alt={alt || name}
          onLoadingStatusChange={(status) => status === 'error' && turnOn()}
          draggable={false}
          className={cn(
            // we use import so classNames that would change the size via Root don't mess here!
            'aspect-square !h-full !w-full object-cover',
            format === 'circle' ? 'rounded-full' : 'rounded-lg',
            className,
            'border-0',
          )}
        />

        {displayInitials && (
          <AvatarPrimitive.Fallback
            draggable={false}
            className={cn(
              // we use import so classNames that would change the size via Root don't mess here!
              'flex !h-full !w-full shrink-0 select-none items-center justify-center bg-muted font-medium leading-none text-muted-foreground',
              format === 'circle' ? 'rounded-full' : 'rounded-lg',
              sizeClass.initials,
              className,
              'border-0',
            )}
          >
            {fallbackLabel ?? getInitials(name)}
          </AvatarPrimitive.Fallback>
        )}
      </AvatarPrimitive.Root>
    );
  },
);

Avatar.displayName = 'Avatar';
